import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Loader from '../../services/loader';
import Notifications from '../../services/notifications';

const DEFAULT_POLL_INTERVAL = 2000;
const DEFAULT_MAX_RETRIES = 10;
const DEFAULT_BACKOFF_CAP = 30000;
const DEFAULT_STATUS_TIMEOUT = 30000;

/**
 * Drive a server-side async export job: enqueue → poll progress → download.
 *
 * Mirrors the admin_portal hook at
 * web_admin_portal/react/services/hooks/useAsyncExport.js — same backend
 * contract from BravonCoreKit api_job_worker.
 *
 * BRAVON-6231 migrates user_portal screens (commissions, scorm, journey)
 * onto the async /jobs + /excel-jobs flow so heavy exports run off the
 * request thread.
 */
export default function useAsyncExport({
  t,
  csrf,
  enqueueUrl,
  statusUrl,
  downloadUrl,
  loaderContainerId,
  pollInterval = DEFAULT_POLL_INTERVAL,
  maxRetries = DEFAULT_MAX_RETRIES,
}) {
  const loader = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const [isExporting, setIsExporting] = useState(false);

  // Mount/unmount tracker — kept separate from loader init so changes to
  // loaderContainerId don't accidentally flip isMountedRef to false on the
  // cleanup pass and leave it stuck.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (loaderContainerId && !loader.current) {
      loader.current = new Loader(`#${loaderContainerId}`);
    }
  }, [loaderContainerId]);

  useEffect(() => {
    if (!loader.current) return;
    if (isExporting) {
      loader.current.show();
    } else {
      loader.current.hide();
    }
  }, [isExporting]);

  const pollJob = useCallback(
    (jobId, retryCount = 0) => {
      if (!isMountedRef.current) return;
      axios
        .get(statusUrl(jobId), { timeout: DEFAULT_STATUS_TIMEOUT })
        .then(res => {
          if (!isMountedRef.current) return;
          const { progress, finishedOn, failedReason } =
            res.data.data || res.data;
          if (loader.current) {
            loader.current.showProgressLoader(
              `${progress?.toFixed(0) || 0}% ${t('order_status_processing')}...`,
            );
          }
          if (finishedOn) {
            setIsExporting(false);
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            if (failedReason) {
              Notifications.showNotificationError('Export failed', failedReason);
            } else {
              Notifications.showNotificationSuccess(
                'Export',
                'Export completed. Downloading...',
              );
              const link = document.createElement('a');
              link.href = downloadUrl(jobId);
              link.download = '';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          } else {
            pollingTimeoutRef.current = setTimeout(
              () => pollJob(jobId),
              pollInterval,
            );
          }
        })
        .catch(() => {
          if (!isMountedRef.current) return;
          if (retryCount < maxRetries) {
            const backoffDelay = Math.min(
              pollInterval * 2 ** retryCount,
              DEFAULT_BACKOFF_CAP,
            );
            pollingTimeoutRef.current = setTimeout(
              () => pollJob(jobId, retryCount + 1),
              backoffDelay,
            );
            return;
          }
          setIsExporting(false);
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          Notifications.showNotificationError(
            'Export',
            'Failed to check export job status. The job may still be processing.',
          );
        });
    },
    [statusUrl, downloadUrl, pollInterval, maxRetries, t],
  );

  const startExport = useCallback(
    (params = {}) => {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(
          // eslint-disable-next-line no-unused-vars
          ([_, value]) => value !== null && value !== undefined && value !== '',
        ),
      );
      setIsExporting(true);
      axios
        .post(enqueueUrl, cleanParams, {
          headers: { 'X-CSRF-Token': csrf },
        })
        .then(response => {
          const jobId = response?.data?.data?.id;
          if (!jobId) {
            setIsExporting(false);
            Notifications.showNotificationError(
              'Export',
              'Failed to start export job.',
            );
            return;
          }
          pollJob(jobId);
        })
        .catch(() => {
          setIsExporting(false);
          Notifications.showNotificationError(
            'Export',
            'Failed to start export job.',
          );
        });
    },
    [enqueueUrl, csrf, pollJob],
  );

  return { startExport, isExporting };
}
